import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { cn } from "@bednbody/ui/lib/utils";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { useMemo, useState } from "react";
import type { useAttendance } from "../ops-dashboard.hooks";

type AttendanceRows = NonNullable<ReturnType<typeof useAttendance>["data"]>;

type StaffState = "on_job" | "idle" | "off";

const STATE_META: Record<StaffState, { label: string; dot: string; badge: string }> = {
  on_job: { label: "On job", dot: "bg-info", badge: "bg-info/15 text-info" },
  idle: { label: "Idle", dot: "bg-success", badge: "bg-success/15 text-success" },
  off: {
    label: "Clocked out",
    dot: "bg-muted-foreground/50",
    badge: "bg-muted text-muted-foreground",
  },
};

const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "therapist", label: "Therapists" },
  { key: "rider", label: "Riders" },
] as const;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Field-staff availability board. State is derived, not stored: clocked-in +
 * an active booking = on job; clocked-in without one = idle; otherwise
 * clocked out. GPS "current location" deliberately isn't shown here — live
 * positions belong to /live-map (broadcast stream), not a polled list.
 */
export function StaffAvailabilityList({
  attendance,
  isLoading,
  failed = false,
}: {
  attendance: AttendanceRows;
  isLoading: boolean;
  /** A failed read is not an empty roster. */
  failed?: boolean;
}) {
  const [roleFilter, setRoleFilter] = useState<(typeof ROLE_FILTERS)[number]["key"]>("all");

  const rows = useMemo(() => {
    const field = attendance.filter((a) => a.role === "therapist" || a.role === "rider");
    const withState = field.map((a) => ({
      ...a,
      state: (a.attendance?.onDuty ? (a.currentBooking ? "on_job" : "idle") : "off") as StaffState,
    }));
    const order: Record<StaffState, number> = { on_job: 0, idle: 1, off: 2 };
    return withState
      .filter((a) => roleFilter === "all" || a.role === roleFilter)
      .sort((a, b) => order[a.state] - order[b.state] || a.name.localeCompare(b.name));
  }, [attendance, roleFilter]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-8 motion-reduce:animate-none" />
            <Skeleton className="h-3 flex-1 motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    );
  }

  // Before the header, not just the list: "0/0 on duty" read off a failed
  // query is the same false statement in a smaller font.
  if (failed) {
    return (
      <ReadFailedCard message="Attendance could not be read, so nobody here is confirmed on or off duty." />
    );
  }

  const onDuty = rows.filter((r) => r.state !== "off").length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setRoleFilter(f.key)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                roleFilter === f.key
                  ? "bg-primary/10 text-primary-dark dark:text-primary-light"
                  : "text-muted-foreground hover:bg-background",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          {onDuty}/{rows.length} on duty
        </span>
      </div>

      {rows.length === 0 ? (
        <Empty>No field staff on the roster yet.</Empty>
      ) : (
        <ul className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {rows.map((s) => {
            const meta = STATE_META[s.state];
            return (
              <li
                key={s.id}
                className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-background"
              >
                <div className="relative shrink-0">
                  <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary-dark dark:text-primary-light">
                    {initials(s.name)}
                  </div>
                  <span
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-card",
                      meta.dot,
                    )}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{s.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    <span className="capitalize">{s.role}</span>
                    {s.hubName ? ` · ${s.hubName}` : ""}
                    {s.currentBooking ? ` · ${s.currentBooking}` : ""}
                    {s.state !== "off" && s.startedTime ? `  · since ${s.startedTime}` : ""}
                  </p>
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded-full px-2 py-0.5 type-body-micro font-medium",
                    meta.badge,
                  )}
                >
                  {meta.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
